import api from './api';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ReviewEntityType = 'wine' | 'producer' | 'region' | 'grape' | 'wine_name';

export const REVIEW_TYPE_LABELS: Record<ReviewEntityType, string> = {
  wine:      'Wine',
  producer:  'Producer',
  region:    'Region',
  grape:     'Grape',
  wine_name: 'Wine Name',
};

// Types that support reject (soft-delete) on the backend.
// As of U1H all five identity types have an `active` field, so reject is
// supported across the board.
export const REJECT_SUPPORTED: Set<ReviewEntityType> = new Set<ReviewEntityType>([
  'wine', 'wine_name', 'producer', 'region', 'grape',
]);

export type ReviewItemSource = 'admin' | 'venue_request' | 'import';

export interface ReviewItemCreatedBy {
  _id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface ReviewQueueItem {
  id: string;
  type: ReviewEntityType;
  displayName: string;
  keyFields: Record<string, unknown>;
  active: boolean | null;
  /** Provenance — populated for records created via venue request or import. */
  source?: ReviewItemSource;
  createdBy?: ReviewItemCreatedBy | null;
  createdAt: string;
}

export interface ReviewQueueSummary {
  total: number;
  wine: number;
  producer: number;
  region: number;
  grape: number;
  wine_name: number;
}

export interface ReviewQueueResponse {
  summary: ReviewQueueSummary;
  items: ReviewQueueItem[];
  pagination: { page: number; limit: number; total: number };
}

// ── Edit URL helper ───────────────────────────────────────────────────────────

export function getEditUrl(type: ReviewEntityType, id: string): string {
  switch (type) {
    case 'wine':      return `/admin/wines/edit/${id}`;
    case 'producer':  return `/admin/producers/edit/${id}`;
    case 'region':    return `/admin/regions/${id}/edit`;
    case 'grape':     return `/admin/grapes/edit/${id}`;
    case 'wine_name': return `/admin/wine-names/edit/${id}`;
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

const BASE = '/admin/governance/review-queue';

const reviewQueueService = {
  getQueue: async (
    type?: ReviewEntityType,
    page = 1,
    limit = 50,
  ): Promise<ReviewQueueResponse> => {
    const params: Record<string, string | number> = { page, limit };
    if (type) params.type = type;
    const res = await api.get<ReviewQueueResponse>(BASE, { params });
    return res.data;
  },

  approve: async (type: ReviewEntityType, id: string): Promise<ReviewQueueItem> => {
    const res = await api.patch<ReviewQueueItem>(`${BASE}/${type}/${id}/approve`);
    return res.data;
  },

  reject: async (type: ReviewEntityType, id: string): Promise<ReviewQueueItem> => {
    const res = await api.patch<ReviewQueueItem>(`${BASE}/${type}/${id}/reject`);
    return res.data;
  },
};

export default reviewQueueService;
